/**
 * Onboarding completed route guard
 *
 * Wraps in-flow routes after phone verification and redirects patients whose
 * `webAppOnboarding.state` is already `completed` to the install screen.
 *
 * Used in `routing/OnboardingRouter.tsx` inside `OnboardingAuthGuard` but outside
 * the install route so completed users can still open install.
 *
 * References:
 * - `services/onboardingProgress.ts` (`isWebAppOnboardingCompleted`)
 * - `store/onboardingStore.ts`
 */

import { Navigate, Outlet } from 'react-router-dom';
import { OnboardingFlowLoadingScreen } from './components/layout/OnboardingFlowLoadingScreen';
import { isOnboardingCompletedRedirectEnforced, isWebAppOnboardingCompleted } from './services/onboardingProgress';
import { getSignedUpPatient, getSignedUpPatientStatus, useOnboardingStore } from './store/onboardingStore';

const INSTALL_PATH = '/webAppOnboarding/install';

/**
 * Renders child routes, a loading screen, or a redirect to install when onboarding is done.
 *
 * @return {Object} React element: outlet, loading screen, or navigate to install.
 */
export function OnboardingCompletedGuard() {
  const signedUpPatient = useOnboardingStore(getSignedUpPatient);
  const signedUpPatientStatus = useOnboardingStore(getSignedUpPatientStatus);

  if (signedUpPatientStatus === 'idle' || signedUpPatientStatus === 'loading') {
    return <OnboardingFlowLoadingScreen />;
  }

  if (isOnboardingCompletedRedirectEnforced(signedUpPatient?.id) && isWebAppOnboardingCompleted(signedUpPatient)) {
    return <Navigate to={INSTALL_PATH} replace />;
  }

  return <Outlet />;
}
